/**
 * Final-results-only dictation (pure helpers).
 *
 * CrewPilot asks the browser for FINAL results only (`interimResults = false`) and never shows or
 * reconciles in-progress guesses. The recognition engine decides when a phrase is final; each final
 * result is appended to the transcript exactly once. There is deliberately NO text de-duplication:
 * "very very important" stays "very very important".
 */

/**
 * New finalized segments in a SpeechRecognitionEvent: results from `resultIndex` on that are final
 * and whose index has not been consumed in this session yet. `seen` (a Set of result indexes) is
 * updated. Identity is the result index, never the words.
 */
export function collectNewFinals(event, seen) {
  const results = event?.results;
  if (!results) return [];
  const start = Number.isInteger(event.resultIndex) ? event.resultIndex : 0;
  const segments = [];
  for (let index = start; index < results.length; index += 1) {
    const result = results[index];
    if (!result?.isFinal || seen.has(index)) continue;
    seen.add(index);
    const text = String(result[0]?.transcript ?? "").trim();
    if (text) segments.push(text);
  }
  return segments;
}

/** Append segments to the existing text with single spaces; existing text is never rewritten. */
export function appendSegments(existing, segments) {
  const base = String(existing ?? "");
  if (!segments.length) return base;
  const joined = segments.join(" ");
  if (!base.trim()) return joined;
  return /\s$/.test(base) ? `${base}${joined}` : `${base} ${joined}`;
}

const SENTENCE_END = /[.!?…]["')\]]*$/;

/** True when the transcript already ends a sentence. Engine punctuation is left alone. */
export function endsWithSentencePunctuation(text) {
  return SENTENCE_END.test(String(text ?? "").trim());
}

function capitalizeSentenceStart(segment) {
  const text = String(segment ?? "");
  const first = text.charAt(0);
  if (!first || first.toLowerCase() === first.toUpperCase()) return text;
  if (first === first.toUpperCase()) return text;
  return first.toUpperCase() + text.slice(1);
}

/**
 * Append finalized phrases for a user-controlled dictation session.
 * A new engine result after an unpunctuated phrase starts a new sentence.
 * Words already spoken are not rewritten, and engine punctuation is kept.
 */
export function appendSpokenSegments(existing, segments) {
  let base = String(existing ?? "");
  for (const raw of segments) {
    let piece = String(raw ?? "").trim();
    if (!piece) continue;
    if (base.trim() && !endsWithSentencePunctuation(base)) {
      base = `${base.trimEnd()}.`;
    }
    if (!base.trim() || endsWithSentencePunctuation(base)) {
      piece = capitalizeSentenceStart(piece);
    }
    base = appendSegments(base, [piece]);
  }
  return base;
}

/**
 * Close the utterance the recognition engine just finished.
 * Adds a period only when that new tail has no sentence punctuation of its own.
 */
export function closeUtterance(text, startLength) {
  const base = String(text ?? "");
  const start = Math.max(0, Number(startLength) || 0);
  if (base.length <= start || !base.slice(start).trim()) return base;
  if (endsWithSentencePunctuation(base)) return base;
  return `${base.trimEnd()}.`;
}
